import React, { useState, useEffect } from "react";
import pflLogoNoMargin from "../assets/tutorial/PfL-logo-no-margin-2025.svg";

interface Feature {
  id: string;
  title: string;
  description: string;
}

interface InteractiveFeatureGridProps {
  features?: Feature[];
}

const defaultFeatures: Feature[] = [
  {
    id: "root-causes",
    title: "Addresses the root causes of challenges",
    description:
      "Goes beyond the \nsurface symptoms to enable sustainable change rather than just temporary relief.",
  },
  {
    id: "engagement",
    title: "Promotes engagement and personalization",
    description:
      "Build custom, individualized toolboxes of life skills, to enhance their application and everyday use.",
  },
  {
    id: "wide-range",
    title: "Covers a wide range of challenges",
    description:
      "Provides practical \nlife skills to address the variety of hurdles we all face across domains \nof life.",
  },
  {
    id: "ongoing-support",
    title: "Provides ongoing support",
    description:
      "Available 24/7, on demand, for when your needs change and new challenges arise.",
  },
  {
    id: "mastery-skills",
    title: "Builds broadly relevant mastery skills",
    description:
      "By learning how to manage challenges in one area, you will become better at managing challenges in others.",
  },
  {
    id: "nurtures-success",
    title: "Nurtures success",
    description:
      "Provides tools to effectively navigate everyday challenges of well-being, relationships, parenting, and career.",
  },
  {
    id: "evidence-based",
    title: "Always deeply evidence-based",
    description:
      "Built by leading scientists based on research by leading scientists.",
  },
  {
    id: "human-centered",
    title: "Fundamentally human-centered",
    description:
      "Curated by humans and communicated by humans. We value that you're human.",
  },
];

const InteractiveFeatureGrid = ({
  features: customFeatures,
}: InteractiveFeatureGridProps) => {
  const features = customFeatures || defaultFeatures;
  const [hoveredFeature, setHoveredFeature] = useState<Feature | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const handleFeatureHover = (feature: Feature) => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    setIsFadingOut(false);
    setHoveredFeature(feature);
  };

  const handleFeatureLeave = () => {
    setIsFadingOut(true);

    const newTimeoutId = setTimeout(() => {
      setHoveredFeature(null);
      setIsFadingOut(false);
      setTimeoutId(null);
    }, 600); // Match the animation duration

    setTimeoutId(newTimeoutId);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  // Calculate positions for circular arrangement
  const radius = 80; // Distance from center
  const centerX = 50; // Percentage from left
  const centerY = 50; // Percentage from top

  const getButtonPosition = (index: number) => {
    // Define positions for NNW, NNE, ENE, ESE, SSE, SSW, WSW, WNW layout (shifted counter-clockwise)
    const positions = [
      { angle: -112.5, radius: radius / 2, xOffset: -5, yOffset: 0 }, // NNW (index 0) - "one stop shop" - move left
      { angle: -67.5, radius: radius / 2, xOffset: 5, yOffset: 0 }, // NNE (index 1) - "root causes" - move right
      { angle: -22.5, radius: radius / 2, xOffset: 2.5, yOffset: 2.5 }, // ENE (index 2) - "mastery skills" - move down and right
      { angle: 22.5, radius: radius / 2, xOffset: 2.5, yOffset: -2.5 }, // ESE (index 3) - "engagement" - move up and right
      { angle: 67.5, radius: radius / 2, xOffset: 5, yOffset: 0 }, // SSE (index 4) - "ongoing support" - move right
      { angle: 112.5, radius: radius / 2, xOffset: -5, yOffset: 0 }, // SSW (index 5) - "human-centered" - move left (symmetrical to index 0)
      { angle: 157.5, radius: radius / 2, xOffset: -2.5, yOffset: -2.5 }, // WSW (index 6) - "evidence-based" - move up and left
      { angle: -157.5, radius: radius / 2, xOffset: -2.5, yOffset: 2.5 }, // WNW (index 7) - "nurtures success" - move down and left
    ];

    const position = positions[index];
    const radians = (position.angle * Math.PI) / 180;

    let x = centerX + position.radius * Math.cos(radians);
    let y = centerY + position.radius * Math.sin(radians);

    // Apply offsets
    x += position.xOffset;
    y += position.yOffset;

    return { x, y };
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto overflow-visible">
      {/* Desktop Circular Layout - Hidden on mobile */}
      <div className="hidden min-[601px]:block relative w-full aspect-square max-w-3xl mx-auto overflow-visible py-16">
        {/* Central Logo/Content Area */}
        <div className="absolute inset-0 flex justify-center items-center">
          <div className="w-64 h-64 rounded-full flex items-center justify-center relative">
            {/* Logo - Always visible */}
            <img
              src={pflLogoNoMargin}
              alt="Psych For Life logo"
              className="w-64 h-64"
            />
            {/* Description Text - Fades in/out over logo */}
            {(hoveredFeature || isFadingOut) && (
              <div
                className={`absolute w-64 h-64 rounded-full bg-electrum text-white flex items-center justify-center p-6 ${
                  isFadingOut ? "animate-fadeOut" : "animate-fadeIn"
                }`}
              >
                <p className="text-base leading-relaxed text-center font-bold whitespace-pre-line">
                  {hoveredFeature?.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Circular Feature Buttons */}
        {features.map((feature, index) => {
          const position = getButtonPosition(index);
          return (
            <button
              key={feature.id}
              onMouseEnter={() => handleFeatureHover(feature)}
              onMouseLeave={handleFeatureLeave}
              className={`
                  absolute w-52 h-32 p-3.5 rounded-2xl border-2 transition-all duration-200 text-center
                  transform -translate-x-1/2 -translate-y-1/2
                  ${
                    hoveredFeature?.id === feature.id
                      ? "bg-electrum text-white border-electrum shadow-lg"
                      : "bg-white text-electrum border-electrum hover:bg-electrum-50 hover:shadow-md"
                  }
                `}
              style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
              }}
            >
              <h4 className="text-base font-semibold leading-tight">
                {feature.title}
              </h4>
            </button>
          );
        })}
      </div>

      {/* Mobile Grid Layout - Visible only on mobile */}
      <div className="block min-[601px]:hidden">
        {/* Central Logo/Content Area */}
        <div className="flex justify-center items-center mb-8">
          <div className="w-full h-32 flex items-center justify-center relative">
            {/* Logo - Always visible */}
            <img
              src={pflLogoNoMargin}
              alt="Psych For Life logo"
              className="w-32 h-32"
            />
            {/* Description Text - Simple show/hide on mobile */}
            {hoveredFeature && (
              <div className="absolute w-full h-32 bg-electrum text-white rounded-xl p-4 flex items-center justify-center">
                <p className="text-sm leading-relaxed text-center font-bold">
                  {hoveredFeature.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Feature Buttons Grid */}
        <div className="grid grid-cols-2 gap-3">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => {
                if (hoveredFeature?.id === feature.id) {
                  // If clicking the same feature, toggle it off
                  setHoveredFeature(null);
                  setIsFadingOut(false);
                } else {
                  // If clicking a different feature, show it
                  setHoveredFeature(feature);
                  setIsFadingOut(false);
                }
              }}
              className={`
                p-3 rounded-xl border-2 transition-all duration-200 text-center
                ${
                  hoveredFeature?.id === feature.id
                    ? "bg-electrum text-white border-electrum shadow-lg"
                    : "bg-white text-electrum border-electrum hover:bg-electrum-50 hover:shadow-md"
                }
              `}
            >
              <h4 className="text-xs font-semibold leading-tight">
                {feature.title}
              </h4>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveFeatureGrid;
